import React from 'react';
import { ClockIcon, EyeOffIcon, RepeatIcon, RouteIcon, TriangleAlertIcon, UsersIcon, XIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { arrivalLines, CostIcon, costs, finalCost, sessionLines } from '../data/copy';
const costIcons: Record<CostIcon, typeof ClockIcon> = {
  clock: ClockIcon,
  repeat: RepeatIcon,
  route: RouteIcon,
  alert: TriangleAlertIcon,
  users: UsersIcon,
  eye: EyeOffIcon
};
export function ProblemSection() {
  return <section className="defer-paint w-full bg-navy py-14 sm:py-20" aria-labelledby="problem-heading" data-id="element-167" data-mp="35gkjf55uymbx">
      <div className="mx-auto max-w-6xl px-6" data-id="element-168" data-mp="3jftll0x8bfww">
        <SectionHeading eyebrow="The real problem" eyebrowTone="danger" title={<span id="problem-heading" data-id="element-170" data-mp="3egpgomhc84cd">Does this sound familiar?</span>} data-id="element-169" data-mp="3jfjvzeg3jarq" />

        <div className="mx-auto mt-12 grid max-w-5xl gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-171" data-mp="1p2152ngkmqfe">
          <div data-id="element-172" data-mp="1p3vn9u0gk4fs">
            <p className="font-display text-base font-semibold uppercase tracking-[0.2em] text-muted" data-id="element-173" data-mp="1k37pmphq4pcl">
              Training starts at
            </p>
            <p className="font-display text-7xl font-extrabold leading-none text-accent sm:text-8xl" data-id="element-174" data-mp="jeojagxzi0qv">
              6:00 PM.
            </p>
            <ul className="mt-6 space-y-1.5 text-lg text-fg/90" data-id="element-175" data-mp="sdtd59ylp5wp">
              {arrivalLines.map(line => <li key={line} data-id="element-176" data-mp="x4i5ssm0o8xf">{line}</li>)}
            </ul>
            <p className="mt-6 font-display text-2xl font-bold uppercase leading-tight text-fg sm:text-3xl" data-id="element-177" data-mp="x6314kg5nvwc">
              And you are still trying to decide what to coach.
            </p>
            <p className="mt-5 leading-relaxed text-muted" data-id="element-178" data-mp="171ei79f1df79">
              You spent part of the day searching YouTube, saving drills, taking
              screenshots, and trying to connect exercises from different
              sources.
            </p>
          </div>

          <div className="lg:border-l lg:border-line lg:pl-16" data-id="element-179" data-mp="3dtnn40y8ip3g">
            <p className="font-display text-3xl font-bold uppercase leading-tight text-fg sm:text-4xl" data-id="element-180" data-mp="3dv8fpl0znxyo">
              Then the session begins.
            </p>
            <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-181" data-mp="p1btq4fqzkx3">
              {sessionLines.map(line => <li key={line} className="flex items-start gap-3 py-3.5 text-[1.02rem] text-fg/90" data-id="element-182" data-mp="k2h9cy24toav">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-danger/15" aria-hidden="true" data-id="element-183" data-mp="y1qcaw1bulww">
                    <XIcon className="h-3 w-3 text-danger" strokeWidth={3} data-id="element-184" data-mp="u1pyndm1yeqw" />
                  </span>
                  {line}
                </li>)}
            </ul>
            <p className="mt-6 text-muted" data-id="element-185" data-mp="12z8mepr2z76h">And after spending hours preparing...</p>
            <p className="mt-1 font-display text-3xl font-extrabold uppercase leading-tight text-fg sm:text-4xl" data-id="element-186" data-mp="1hnwg0qhd96ts">
              the session still feels <span className="text-danger" data-id="element-187" data-mp="1crxynmuggp2v">improvised.</span>
            </p>
          </div>
        </div>

        <div className="mt-16 sm:mt-24" data-id="element-188" data-mp="1qn9sls0sebiq">
          <h3 className="mx-auto max-w-3xl text-center font-display text-3xl font-extrabold uppercase leading-tight text-fg sm:text-4xl" data-id="element-189" data-mp="1lraydnss1zae">
            The real cost of not having the right system
          </h3>

          <ul className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-3 sm:gap-4" data-id="element-190" data-mp="2aad1bf8dm4oq">
            {costs.map(cost => {
            const Icon = costIcons[cost.icon];
            return <li key={cost.title} className="flex flex-col gap-3 rounded-3xl border border-line bg-ink p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-5" data-id="element-191" data-mp="20cykvfmgdfwf">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-danger/10 sm:h-12 sm:w-12" aria-hidden="true" data-id="element-192" data-mp="1mkipn0zsmm62">
                    <Icon className="h-5 w-5 text-danger sm:h-6 sm:w-6" strokeWidth={1.9} data-id="element-193" data-mp="1co5tzxkoty8o" />
                  </span>
                  <div className="min-w-0" data-id="element-194" data-mp="1hns9yerwhsl6">
                    <p className="font-display text-base font-bold uppercase leading-[1.1] text-fg sm:text-xl" data-id="element-195" data-mp="1hknu2k52p8eo">
                      {cost.title}
                    </p>
                    {cost.detail.map(line => <p key={line} className="mt-1 text-[0.8rem] leading-snug text-muted sm:text-[0.92rem]" data-id="element-196" data-mp="18o549gs520mb">
                        {line}
                      </p>)}
                  </div>
                </li>;
          })}
          </ul>

          <div className="mt-5 flex flex-col items-center gap-3 rounded-3xl border-2 border-accent/40 bg-accent/[0.06] px-6 py-9 text-center sm:px-10 sm:py-12" data-id="element-197" data-mp="qv1iqfknm44s">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/15" aria-hidden="true" data-id="element-198" data-mp="mrvfzhwr2v32">
              <div className="h-7 w-7 text-accent" strokeWidth={1.9} data-id="element-199" data-mp="cvs4vmjx7m3z" />
            </span>
            <p className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight text-fg sm:text-3xl" data-id="element-200" data-mp="cy4u8ks0cyr2">
              {finalCost.title}
            </p>
            <p className="text-muted" data-id="element-201" data-mp="3wojud170wmu">{finalCost.lead}</p>
            <p className="mt-1 font-display text-4xl font-extrabold leading-[1.05] text-accent sm:text-5xl" data-id="element-202" data-mp="22agj7tc2ne4n">
              {finalCost.question}
            </p>
          </div>
        </div>
      </div>
    </section>;
}